import { useId, cloneElement } from 'react'
import type { CSSProperties, ReactElement } from 'react'

export interface FieldProps {
  /** Label text displayed above the input. Always top-aligned — no floating labels. */
  label: string
  /**
   * Wires the `<label htmlFor>` to the child `id`. Auto-generated via `useId()`
   * when omitted. Pass explicitly to share an id with external elements.
   */
  htmlFor?: string
  /** Appends a clay-coloured * after the label. The `required` attribute on the
   *  input is the screen-reader signal — the asterisk is `aria-hidden`. */
  required?: boolean
  /** Visually hides the label while keeping it in the accessibility tree
   *  (still wired to the input). Use when a nearby eyebrow/heading already
   *  names the field and a second visible label would be redundant. */
  hideLabel?: boolean
  /**
   * Error message shown below the input in clay colour. When present it replaces
   * `helperText` and sets `aria-invalid="true"` + `aria-describedby` on the child.
   *
   * This primitive does NOT validate — callers set `error` based on their own
   * timing (typically on blur or on submit).
   */
  error?: string | undefined
  /** Supplementary text shown below the input when there is no `error`. */
  helperText?: string | undefined
  /**
   * Optional context copy between the label row and the input. Use for longer
   * explanations that don't fit the label (e.g. policy notes).
   */
  description?: string | undefined
  /** Single input element. Field clones it to inject `id`, `aria-describedby`,
   *  and `aria-invalid` — do not pass these props yourself. */
  children: ReactElement
  /** Pass-through className on the outer wrapper div. */
  className?: string
}

const wrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}

const labelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  lineHeight: 1.4,
}

const requiredStyle: CSSProperties = {
  color: 'var(--danger)',
  lineHeight: 1,
}

/** Screen-reader-only: removes the label from the visual flow (no layout
 *  footprint) while keeping it available to assistive tech. */
const visuallyHiddenStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

const descriptionStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--text-2)',
  lineHeight: 1.4,
  marginBottom: 4,
}

const helperStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-2)',
  lineHeight: 1.4,
  marginTop: 4,
}

const errorStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--danger)',
  lineHeight: 1.4,
  marginTop: 4,
  animation: 'lms-fade-in var(--motion-duration-base) var(--motion-ease-out)',
}

/**
 * Canonical form field wrapper: label + input + helper/error text.
 *
 * Handles id wiring, aria-describedby, and aria-invalid automatically.
 * Validation timing is the caller's responsibility — set `error` on blur or
 * on submit, not on every keystroke.
 *
 * @example
 * <Field label="Reason" required helperText="Why are you taking this leave?">
 *   <textarea rows={4} ... />
 * </Field>
 *
 * <Field label="Email" error="Must be a valid email">
 *   <input type="email" ... />
 * </Field>
 */
export function Field({
  label,
  htmlFor,
  required = false,
  hideLabel = false,
  error,
  helperText,
  description,
  children,
  className,
}: FieldProps): JSX.Element {
  const generatedId = useId()
  const inputId = htmlFor ?? generatedId
  const errorId = `${inputId}-error`
  const helperId = `${inputId}-helper`

  const hasError = Boolean(error)
  const hasHelper = Boolean(helperText) && !hasError

  const describedBy = hasError ? errorId : hasHelper ? helperId : undefined

  const clonedChild = cloneElement(children, {
    id: inputId,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(hasError ? { 'aria-invalid': 'true' as const } : {}),
  })

  return (
    <div style={wrapperStyle} className={className}>
      <label htmlFor={inputId} style={hideLabel ? visuallyHiddenStyle : labelStyle}>
        {label}
        {required ? (
          <span aria-hidden style={requiredStyle}>
            *
          </span>
        ) : null}
      </label>

      {description ? <p style={descriptionStyle}>{description}</p> : null}

      {clonedChild}

      {hasError ? (
        <span id={errorId} role="alert" style={errorStyle}>
          {error}
        </span>
      ) : null}

      {hasHelper ? (
        <span id={helperId} style={helperStyle}>
          {helperText}
        </span>
      ) : null}
    </div>
  )
}
